import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { StarInput } from "@/components/reviews/Stars";

const MAX = 1000;

// "Donner mon avis" (create) or edit of the device's own review.
export const ReviewForm = ({ url, mine, onSaved, onCancel }) => {
  const { t, lang } = useI18n();
  const [pseudo, setPseudo] = useState(mine?.pseudo || "");
  const [stars, setStars] = useState(mine?.stars || 0);
  const [text, setText] = useState(mine?.text || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stars || text.length > MAX) return setError(t("reviews.invalid"));
    if (!pseudo.trim()) return setError(t("err.reviewPseudo"));
    setBusy(true);
    setError("");
    try {
      const body = { pseudo, stars, text, lang };
      const { data } = mine ? await api.put(`${url}/mine`, body) : await api.post(url, body);
      toast.success(t(mine ? "reviews.updated" : "reviews.published"));
      onSaved(data);
    } catch (err) {
      setError(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="min-w-0 space-y-3 rounded-2xl border border-[rgba(245,197,66,0.3)] bg-black/30 p-4" data-testid="review-form">
      <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)]">{t(mine ? "reviews.editTitle" : "reviews.formTitle")}</h3>
      <StarInput value={stars} onChange={setStars} />
      <Input value={pseudo} maxLength={40} placeholder={t("reviews.pseudo")} onChange={(e) => setPseudo(e.target.value)} aria-label={t("reviews.pseudo")} data-testid="review-pseudo-input" />
      <div>
        <Textarea rows={4} value={text} placeholder={t("reviews.comment")} onChange={(e) => setText(e.target.value)} aria-label={t("reviews.comment")} data-testid="review-text-input" />
        <p className={`mt-1 text-end text-[0.7rem] ${text.length > MAX ? "text-red-300" : "text-neutral-500"}`} data-testid="review-text-counter">{text.length}/{MAX}</p>
      </div>
      {error && <p className="text-sm text-red-300" role="alert" data-testid="review-form-error">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="btn-gold btn-sm" disabled={busy} data-testid="review-submit-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t(mine ? "reviews.save" : "reviews.publish")}
        </button>
        {onCancel && <button type="button" className="btn-red btn-sm" onClick={onCancel} data-testid="review-cancel-btn">{t("form.cancel")}</button>}
      </div>
    </form>
  );
};
